"use client"

import { t } from "@/languages/i18n"
import { TourItem } from "@/types/types"
import { PlusCircleOutlined } from "@ant-design/icons"
import { Button, Form, Input, message, Select, Spin } from "antd"
import Title from "antd/es/typography/Title"
import { useRouter } from "next/navigation"
import React, { useEffect, useState } from "react"
import { fetchAllTourV2s } from "../../tour/services/tours"
import { createDestination } from "../services/destination"

interface FormAddDestinationProps {
  onDestinationAdded?: () => void
}

const FormAddDestination: React.FC<FormAddDestinationProps> = ({ onDestinationAdded }) => {
  const router = useRouter()
  const [form] = Form.useForm()
  const [tours, setTours] = useState<TourItem[]>([])
  const [toursLoading, setToursLoading] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    loadTours()
  }, [])

  const loadTours = async () => {
    setToursLoading(true)
    try {
      const response = await fetchAllTourV2s()
      const data = (response as { data: TourItem[] }).data
      setTours(data || [])
    } catch (error) {
      console.error("Error loading tours:", error)
      message.error(t("Error loading tours!"))
    } finally {
      setToursLoading(false)
    }
  }

  const onFinish = async (values: { name: string; tourIds?: number[] }) => {
    try {
      setLoading(true)
      await createDestination(values)
      message.success(t("Destination created successfully!"))
      form.resetFields()
      if (onDestinationAdded) {
        onDestinationAdded()
      } else {
        router.push("/admin/destinations")
      }
    } catch (error) {
      console.error("Error creating destination:", error)
      message.error(t("Error creating destination!"))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="px-4">
      <div className="flex items-center mb-4">
        <Title level={2} className="mb-0">
          {t("Add New Destination")}
        </Title>
      </div>

      <div className="bg-white rounded-lg shadow p-6 max-w-2xl">
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            name="name"
            label={t("Destination Name")}
            rules={[
              { required: true, message: t("Please enter destination name!") },
              { min: 2, message: t("Destination name must be at least 2 characters!") },
              { max: 100, message: t("Destination name cannot exceed 100 characters!") },
            ]}
          >
            <Input placeholder={t("Enter destination name...")} />
          </Form.Item>

          <Form.Item name="tourIds" label={t("Select Tours")} help={t("Select tours for this destination")}>
            {toursLoading ? (
              <div className="flex justify-center py-4">
                <Spin />
              </div>
            ) : (
              <Select
                mode="multiple"
                placeholder={t("Select tours...")}
                showSearch
                filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                options={tours.map(tour => ({
                  value: tour.id,
                  label: tour.attributes?.name || tour.attributes?.title || t("No tours found"),
                }))}
              />
            )}
          </Form.Item>

          <Form.Item className="mb-0">
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              icon={<PlusCircleOutlined />}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              {t("Create Destination")}
            </Button>
          </Form.Item>
        </Form>
      </div>
    </div>
  )
}

export default FormAddDestination
